import { handleSupabaseList, handleSupabaseSingle, supabase } from '@/lib/supabase-unified'

export interface UserFolder {
  id: number
  created_at: string
  title: string
  user_id: string
  locked?: number | null
}

export interface UserBookmark {
  id: number
  created_at: string
  user_folder_id: number
  course_id?: number
  lesson_id?: number
  old_lesson_id?: number
  user_id: string
  url: string
  title?: string
  lesson_title?: string
  course_title?: string
  lesson_status?: number
  course_status?: number
  sort_order?: number
}

// Get all folders for a user
export async function fetchUserFolders(userId: string): Promise<UserFolder[]> {
  return handleSupabaseList<UserFolder>(
    supabase
      .from('user_folders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false }) as any
  )
}

// Create a new folder
export async function createUserFolder(title: string, userId: string): Promise<UserFolder> {
  return handleSupabaseSingle<UserFolder>(
    supabase
      .from('user_folders')
      .insert({
        title,
        user_id: userId
      })
      .select()
      .single() as any
  );
}

// Get all bookmarks for the user grouped by folder id
export async function fetchAllFolderBookmarks(userId: string): Promise<Record<number, UserBookmark[]>> {
  const data = await handleSupabaseList<any>(
    supabase
      .from('bookmarks')
      .select(
        `id, created_at, user_folder_id, course_id, lesson_id, user_id, url, old_lesson_id, title, sort_order,
         lessons:lesson_id ( title, status ), course:course_id ( title, status )`
      )
      .eq('user_id', userId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }) as any
  )

  const grouped: Record<number, UserBookmark[]> = {}
  data.forEach((row: any) => {
    const b: UserBookmark = {
      id: row.id,
      created_at: row.created_at,
      user_folder_id: row.user_folder_id,
      course_id: row.course_id ?? undefined,
      lesson_id: row.lesson_id ?? undefined,
      old_lesson_id: row.old_lesson_id ?? undefined,
      user_id: row.user_id,
      url: row.url ?? '',
      title: row.title ?? undefined,
      sort_order: row.sort_order ?? undefined,
      lesson_title: row.lessons?.title,
      lesson_status: row.lessons?.status,
      course_title: row.course?.title,
      course_status: row.course?.status
    }
    const folderId = b.user_folder_id
    if (!grouped[folderId]) grouped[folderId] = []
    grouped[folderId].push(b)
  })

  return grouped
}

// Add a lesson to a folder (create bookmark)
export async function addLessonToFolder(
  folderId: number, 
  lessonId: number, 
  userId: string, 
  url: string,
  courseId?: number
): Promise<UserBookmark> {
  const insertData: any = {
    user_folder_id: folderId,
    lesson_id: lessonId,
    user_id: userId,
    url,
    course_id: null,
  }
  
  return handleSupabaseSingle<UserBookmark>(
    supabase
      .from('bookmarks')
      .insert(insertData)
      .select()
      .single() as any
  );
}

// Add a lesson to multiple folders at once
export async function addLessonToMultipleFolders(
  folderIds: number[],
  lessonId: number,
  userId: string,
  url: string,
  courseId?: number
): Promise<{ success: number; failed: number; errors: string[] }> {
  if (folderIds.length === 0) {
    return { success: 0, failed: 0, errors: ['No folders provided'] }
  }

  // Prepare insert data for all folders
  const insertData = folderIds.map(folderId => ({
    user_folder_id: folderId,
    lesson_id: lessonId,
    user_id: userId,
    url,
    course_id: courseId || null,
  }))

  const data = await handleSupabaseList<UserBookmark>(
    supabase
      .from('bookmarks')
      .insert(insertData)
      .select() as any
  )

  const successCount = data.length
  const failedCount = folderIds.length - successCount

  return {
    success: successCount,
    failed: failedCount,
    errors: failedCount > 0 ? [`Failed to add to ${failedCount} folder${failedCount > 1 ? 's' : ''}`] : []
  }
}

// Remove a lesson from a folder (delete bookmark)
export async function removeLessonFromFolder(
  folderId: number, 
  lessonId: number, 
  userId: string
): Promise<boolean> {
  const data = await handleSupabaseList<{ id: number }>(
    supabase
      .from('bookmarks')
      .delete()
      .eq('user_folder_id', folderId)
      .eq('lesson_id', lessonId)
      .eq('user_id', userId)
      .select('id') as any
  )

  return data.length > 0
}

// Lightweight boolean check: does this user have any bookmark for lessonId?
export async function isLessonBookmarked(userId: string, lessonId: number): Promise<boolean> {
  const { count, error } = await supabase
    .from('bookmarks')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('lesson_id', lessonId)

  if (error) {
    throw error
  }

  return (count ?? 0) > 0
}

export async function addCourseToMultipleFolders(
  folderIds: number[],
  courseId: number,
  userId: string,
  url: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  if (folderIds.length === 0) {
    return { success: 0, failed: 0, errors: ['No folders provided'] }
  }

  const insertData = folderIds.map((folderId) => ({
    user_folder_id: folderId,
    lesson_id: null,
    user_id: userId,
    url,
    course_id: courseId,
  }))

  const data = await handleSupabaseList<UserBookmark>(
    supabase.from('bookmarks').insert(insertData).select() as any
  )

  const successCount = data.length
  const failedCount = folderIds.length - successCount

  return {
    success: successCount,
    failed: failedCount,
    errors:
      failedCount > 0
        ? [`Failed to add to ${failedCount} folder${failedCount > 1 ? 's' : ''}`]
        : [],
  }
}

export async function removeCourseFromFolder(
  folderId: number,
  courseId: number,
  userId: string
): Promise<boolean> {
  const data = await handleSupabaseList<{ id: number }>(
    supabase
      .from('bookmarks')
      .delete()
      .eq('user_folder_id', folderId)
      .eq('course_id', courseId)
      .eq('user_id', userId)
      .is('lesson_id', null)
      .select('id') as any
  )
  return data.length > 0
}

export async function isCourseBookmarked(
  userId: string,
  courseId: number
): Promise<boolean> {
  const { count, error } = await supabase
    .from('bookmarks')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .is('lesson_id', null)

  if (error) throw error
  return (count ?? 0) > 0
}

function assertFolderUnlocked(folder: Pick<UserFolder, 'locked' | 'title'>) {
  if (folder.locked === 1) {
    throw new Error(`“${folder.title}” is locked and can’t be changed.`)
  }
}

export async function renameUserFolder(
  folderId: number,
  title: string,
  _userId: string,
): Promise<UserFolder> {
  const trimmed = title.trim()
  if (!trimmed) throw new Error('Please enter a folder name')

  const { data, error } = await supabase.rpc('rename_user_folder', {
    p_folder_id: folderId,
    p_title: trimmed,
  })

  if (error) throw error
  return data as UserFolder
}

export async function deleteUserFolder(
  folderId: number,
  userId: string,
): Promise<void> {
  const existing = await handleSupabaseSingle<UserFolder>(
    supabase
      .from('user_folders')
      .select('*')
      .eq('id', folderId)
      .eq('user_id', userId)
      .single() as any,
  )
  assertFolderUnlocked(existing)

  await handleSupabaseList<{ id: number }>(
    supabase
      .from('bookmarks')
      .delete()
      .eq('user_folder_id', folderId)
      .eq('user_id', userId)
      .select('id') as any,
  )

  await handleSupabaseList<{ id: number }>(
    supabase
      .from('user_folders')
      .delete()
      .eq('id', folderId)
      .eq('user_id', userId)
      .select('id') as any,
  )
}

export async function removeBookmarkById(
  bookmarkId: number,
  userId: string,
): Promise<boolean> {
  const data = await handleSupabaseList<{ id: number }>(
    supabase
      .from('bookmarks')
      .delete()
      .eq('id', bookmarkId)
      .eq('user_id', userId)
      .select('id') as any,
  )
  return data.length > 0
}

/** Move a bookmark to another folder (optional full target order in the same RPC). */
export async function moveBookmarkToFolder(
  bookmarkId: number,
  toFolderId: number,
  _userId: string,
  orderedTargetIds?: number[],
): Promise<'moved' | 'already_exists' | 'merged'> {
  const { data, error } = await supabase.rpc('move_bookmark_to_folder', {
    p_bookmark_id: bookmarkId,
    p_to_folder_id: toFolderId,
    ...(orderedTargetIds && orderedTargetIds.length > 0
      ? { p_ordered_ids: orderedTargetIds }
      : {}),
  })

  if (error) throw error

  const result = data as string
  if (result === 'already_exists' || result === 'merged' || result === 'moved') {
    return result
  }
  return 'moved'
}

/** Persist within-folder order (one request). */
export async function reorderFolderBookmarks(
  folderId: number,
  orderedBookmarkIds: number[],
): Promise<void> {
  if (orderedBookmarkIds.length === 0) return

  const { error } = await supabase.rpc('reorder_folder_bookmarks', {
    p_folder_id: folderId,
    p_bookmark_ids: orderedBookmarkIds,
  })

  if (error) throw error
}

